import * as Sentry from "@sentry/vue";
import {
    BrowserTracing,
    BrowserProfilingIntegration,
    Replay,
} from "@sentry/vue";

window.SalesByteCaptureException = Sentry.captureException;

const {
    VITE_SENTRY_DSN,
    VITE_SENTRY_RELEASE,
    VITE_SENTRY_ENVIRONMENT,
    VITE_SENTRY_TRACES_SAMPLE_RATE,
    VITE_SENTRY_PROFILES_SAMPLE_RATE,
} = import.meta.env;

/**
 * Livewire rejects every pending action promise with `{status, body, json, errors}`
 * when its request doesn't complete. A null status means the request was cancelled
 * (superseded by a newer commit, or navigation) or the fetch itself failed — expected
 * flow control, not an application error.
 */
function isLivewireRequestInterruption(exception) {
    return (
        exception !== null &&
        typeof exception === "object" &&
        !(exception instanceof Error) &&
        "status" in exception &&
        "body" in exception &&
        "json" in exception &&
        "errors" in exception &&
        exception.status === null
    );
}

/**
 * An AxiosError without a response never reached the server (offline, connection
 * drop, page unload) — actionable server errors always carry a response.
 */
function isAxiosConnectivityError(exception) {
    return Boolean(exception?.isAxiosError) && !exception.response;
}

export function initSentry(app = null) {
    if (VITE_SENTRY_DSN) {
        Sentry.init({
            ...(app ? { app } : {}),
            dsn: VITE_SENTRY_DSN,
            release: VITE_SENTRY_RELEASE,
            environment: VITE_SENTRY_ENVIRONMENT,

            integrations: (defaultIntegrations) => [
                ...defaultIntegrations.filter(
                    (integration) => app || integration.name !== "Vue"
                ),
                new BrowserTracing(),
                new BrowserProfilingIntegration(),
                new Replay(),
            ],

            // Performance monitoring
            tracesSampleRate: parseFloat(VITE_SENTRY_TRACES_SAMPLE_RATE) || 0.0,
            profilesSampleRate: parseFloat(VITE_SENTRY_PROFILES_SAMPLE_RATE) || 0.0,

            tracePropagationTargets: [
                /^http:\/\/localhost/,
                /^http:\/\/127.0.0.1/,
                /^\//,
                /^https:\/\/[^\.]+\.salesbyte\.se/,
            ],

            replaysSessionSampleRate: 0.001,
            replaysOnErrorSampleRate: 1.0,

            // Track Vue components
            trackComponents: true,

            beforeSend(event, hint) {
                const exception = hint?.originalException;

                if (
                    isLivewireRequestInterruption(exception) ||
                    isAxiosConnectivityError(exception)
                ) {
                    return null;
                }

                return event;
            },
        });

        // Add axios context to Sentry
        Sentry.addEventProcessor((event, hint) => {
            if (hint && hint.originalException) {
                const ex = hint.originalException;
                if (ex.isAxiosError && ex.response) {
                    event.contexts = event.contexts || {};
                    event.contexts.axios = {
                        Request: {
                            method: ex.response.config.method,
                            url: ex.response.config.url,
                        },
                        Response: {
                            status: ex.response.status,
                        },
                    };
                }
            }
            return event;
        });
    }
}
